import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { ApiError, SessionExpiredError } from './lib/api';
import { addTelemetrySink } from './lib/telemetry';
import './index.css';

/**
 * Query owns anything with a URL. The stream store owns anything with a socket.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // A session that ended is not a transient failure, and a 403 or 404 on
        // this surface is an authorisation decision. Retrying either just
        // delays the correct screen.
        if (error instanceof SessionExpiredError) return false;
        if (error instanceof ApiError && [400, 401, 403, 404, 409].includes(error.httpStatus)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      staleTime: 15_000,
    },
    mutations: { retry: false },
  },
});

// In production this is where a Sentry client would be wired, with the scrub
// deny-list applied in `beforeSend`. Until then the sink is left empty rather
// than pointed at a console in production, which would put unmapped kinds into
// a place a customer can read.
addTelemetrySink(() => {});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
